/**
 * frameImageCache.ts — decode a live-scan frame ONCE.
 *
 * 2026-09-22 (Galaxy S21+ "the scan feels slow"): a single frame's JPEG data
 * URL was being decoded into a fresh <img> by every consumer in turn: the
 * sharpness gate, the stability gate, the species-page probe, the My Pokémon
 * probe, the grid extractor, the pipeline runner's motion hash, and then
 * ONCE MORE PER NEW CELL for registration colour sampling. Six to eighteen
 * full 1280px decodes per frame, on a pipeline whose frame rate is exactly
 * 1 / processFrame. Every one of those callers now comes here and gets the
 * same element back for the same bytes.
 *
 * Two entries, keyed by the data URL string (the current frame and the one
 * before it, which the stability gate still compares against). The entry is
 * the PROMISE, so concurrent callers on the same frame share one decode.
 */
const MAX_ENTRIES = 2;
const cache = new Map<string, Promise<HTMLImageElement>>();
// Every element this cache ever produced. The OCR engine memoises identical
// requests only on THESE (a shared frame asked twice is one native pass); an
// element a caller decoded itself is never memoised, so per-call fallback
// behaviour and tests with fake images stay exactly as they were.
const sharedImages = new WeakSet<HTMLImageElement>();

export function isSharedFrameImage(img: HTMLImageElement): boolean {
  return sharedImages.has(img);
}

function decode(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("frame_decode_failed"));
    img.src = dataUrl;
  });
}

export function loadFrameImage(dataUrl: string): Promise<HTMLImageElement> {
  const hit = cache.get(dataUrl);
  if (hit) return hit;
  const p = decode(dataUrl).then((img) => {
    sharedImages.add(img);
    return img;
  });
  // A failed decode must not poison the cache for a retry.
  p.catch(() => cache.delete(dataUrl));
  cache.set(dataUrl, p);
  if (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  return p;
}

/** Test seam. */
export function __resetFrameImageCacheForTests(): void {
  cache.clear();
}
